import * as React from 'react'
import { Icon } from 'antd'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { Menu, Dropdown, Button, Select, Input, Checkbox } from 'antd-v4'
import { DiscountFilter } from './DiscountFilter'
import { isUndefined } from 'lodash'
import { AsyncProductInput } from '../../../../components/GrInput/AsyncProductInput'
import { IBaseProduct } from '@getgreenline/homi-shared'
import classNames from 'classnames'

interface FilterGroupProps {
  currentCompanyStore?: CurrentCompanyStore
  filter: DiscountFilter
  onSearch: (offset?: number, limit?: number) => void
}

interface FilterGroupState {
  search: string
  loading: boolean
}

@inject('currentCompanyStore')
@observer
export class FilterGroup extends React.Component<FilterGroupProps, FilterGroupState> {
  currentCompanyStore = this.props.currentCompanyStore!

  constructor(props: FilterGroupProps) {
    super(props)

    this.state = {
      search: '',
      loading: false,
    }
  }

  componentDidMount = async () => {
    const { productStore } = this.currentCompanyStore!
    if (!productStore!.categories) {
      await productStore!.getCategories()
    }

    if (this.props.currentCompanyStore?.salesChannels.length === 0) {
      await this.currentCompanyStore?.getSalesChannels()
    }
  }

  get locationOptions() {
    return (this.currentCompanyStore!.locations || []).map((location) => ({
      value: location.id,
      label: location.name,
    }))
  }

  get categoryOptions() {
    return (this.currentCompanyStore!.productStore!.flattenedCategories || []).map((category) => ({
      value: category.id,
      label: category.name,
    }))
  }

  get salesChannelOptions() {
    const { salesChannels, getSaleChannelDisplayName } = this.currentCompanyStore

    return salesChannels.map((channel) => ({
      value: channel.id,
      label: getSaleChannelDisplayName(channel),
    }))
  }

  get salesChannelCount(): number {
    return this.props.filter.salesChannelIds?.length || 0
  }

  get checkboxStyle() {
    return {
      paddingTop: `3px`,
      textAlign: `center` as `center`,
      height: `31px`,
      borderStyle: `solid`,
      borderWidth: `1px`,
      borderColor: `#d9d9d9`,
      padding: `4px 10px`,
    }
  }

  render() {
    const { filter } = this.props
    const isGreenline = this.currentCompanyStore?.company?.permissions.canAccessLoyalty

    return (
      <>
        <div className='mb-2' style={{ display: 'flex', flexWrap: 'wrap' }}>
          <div className='mr-2 mb-2' style={{ width: 200 }}>
            <Input
              value={filter.searchTerm}
              onChange={(e) => filter.setSearchTerm(e.target.value)}
              placeholder={'Discount name, ID'}
              prefix={<Icon type='search' />}
            />
          </div>

          <Dropdown
            className='gr-filter-item gr-filter-gutter'
            trigger={['click']}
            overlay={
              <Menu>
                {this.locationOptions.map((option: any) => {
                  return (
                    <Menu.Item key={option.value} disabled style={{ padding: 0 }}>
                      <Checkbox
                        checked={filter.locationIds?.includes(option.value)}
                        className='w-100'
                        style={{ padding: '5px 12px' }}
                        onChange={(e) => {
                          if (e.target.checked) {
                            filter.addLocationId(option.value)
                          } else {
                            filter.removeLocationId(option.value)
                          }
                        }}
                      >
                        {option.label}
                      </Checkbox>
                    </Menu.Item>
                  )
                })}
              </Menu>
            }
          >
            <Button
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '180px',
              }}
            >
              Locations ({(filter.locationIds || []).length})
              <Icon type='down' />
            </Button>
          </Dropdown>

          <span className='mr-2 mb-2'>
            <AsyncProductInput
              showSearch
              className='gr-filter-item'
              mode='default'
              placeholder='Products'
              filterOption={false}
              dropdownMatchSelectWidth={false}
              allowClear
              selectedProducts={filter.productIds}
              onChange={(products: IBaseProduct[]) => {
                const productIds = products.length > 0 ? [products[0]?.id] : undefined
                filter.setProductIds(productIds)
              }}
            />
          </span>

          <div className='filter-dropdown-input-wrapper mr-2 mb-2'>
            <Select<string>
              options={this.categoryOptions}
              notFoundContent={'No results found'}
              dropdownMatchSelectWidth={false}
              placeholder={'Category'}
              allowClear
              showSearch
              filterOption={(input, option) => {
                const foundIndex = option?.label
                  ?.toString()
                  .toLowerCase()
                  .indexOf(input.toLowerCase())
                return !isUndefined(foundIndex) && foundIndex >= 0
              }}
              value={filter.categoryIds ? filter.categoryIds[0] : undefined}
              onChange={(value) => {
                filter.setCategoryIds(value ? [value] : undefined)
              }}
              style={{ width: '180px' }}
            />
          </div>

          {isGreenline ? (
            <div className='mr-2 mb-2'>
              <div style={this.checkboxStyle}>
                <Checkbox
                  checked={filter.isLoyalty}
                  onChange={(e) => {
                    filter.setIsLoyalty(e.target.checked || undefined)
                  }}
                >
                  Loyalty
                </Checkbox>
              </div>
            </div>
          ) : null}

          <div className='mr-2 mb-2'>
            <div style={this.checkboxStyle}>
              <Checkbox
                checked={filter.isAutomatic}
                onChange={(e) => {
                  filter.setIsAutomatic(e.target.checked || undefined)
                }}
              >
                Automatic
              </Checkbox>
            </div>
          </div>

          {this.props.currentCompanyStore?.canUseSalesChannel && (
            <Dropdown
              className={classNames(
                'gr-filter-item',
                'gr-filter-gutter',
                'gr-filter-dropdown',
                this.salesChannelCount === 0 && 'default-color',
              )}
              trigger={['click']}
              overlay={
                <Menu>
                  {this.salesChannelOptions.map((option) => {
                    return (
                      <Menu.Item key={option.value} disabled style={{ padding: 0 }}>
                        <Checkbox
                          checked={filter.salesChannelIds?.includes(option.value)}
                          className='w-100'
                          style={{ padding: '5px 12px' }}
                          onChange={(e) =>
                            filter.toggleSalesChannel(option.value, e.target.checked)
                          }
                        >
                          {option.label}
                        </Checkbox>
                      </Menu.Item>
                    )
                  })}
                </Menu>
              }
            >
              <Button
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '180px',
                }}
              >
                Sales Channel ({this.salesChannelCount})
                <Icon type='down' />
              </Button>
            </Dropdown>
          )}

          <div className='mr-2 mb-2'>
            <BlazeButton
              buttonProps={{
                type: 'primary',
                loading: this.state.loading,
                onClick: async () => {
                  this.setState({ loading: true })
                  await this.props.onSearch()
                  this.setState({ loading: false })
                },
              }}
              titleProps={{ title: 'Apply' }}
            />
          </div>

          <div className='mr-2 mb-2'>
            <BlazeButton
              buttonProps={{
                onClick: () => {
                  filter.reset()
                  this.props.onSearch()
                },
                disabled: filter.isFilterEmpty,
              }}
              titleProps={{ title: 'Reset' }}
            />
          </div>
        </div>
      </>
    )
  }
}
